import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { appPath } from '../../../utils/appPath'

const DOT_COLORS = ['text-blue-400', 'text-violet-400', 'text-amber-500'] as const

/**
 * Same chrome pattern as `ClientDetailHeaderBar` — back + title / category;
 * period filters on the right at `lg+`.
 */
export interface ProjectDetailHeaderBarProps {
  title: string
  category?: string | null
  /** AM / client / PM from mapping + jsonbyAM (optional). */
  lineageMeta?: {
    accountManager: string | null
    clientName: string | null
    projectManager: string | null
  }
  lineageMetaLoading?: boolean
  /** Optional alert pill (empty period, no data, etc.) */
  statusPill?: string
  periodSlot: React.ReactNode
}

export function ProjectDetailHeaderBar({
  title,
  category,
  lineageMeta,
  lineageMetaLoading,
  statusPill,
  periodSlot,
}: ProjectDetailHeaderBarProps) {
  const lineageSegments = useMemo(() => {
    const m = lineageMeta
    if (!m) return []
    const out: { key: string; label: string; value: string }[] = []
    if (m.accountManager?.trim())
      out.push({
        key: 'am',
        label: 'Account Manager',
        value: m.accountManager.trim(),
      })
    if (m.clientName?.trim())
      out.push({
        key: 'client',
        label: 'Client',
        value: m.clientName.trim(),
      })
    if (m.projectManager?.trim())
      out.push({
        key: 'pm',
        label: 'Project manager',
        value: m.projectManager.trim(),
      })
    return out
  }, [lineageMeta])

  return (
    <div className="flex flex-col gap-2.5 sm:gap-3 lg:flex-row lg:items-center lg:justify-between w-full min-w-0">
      <div className="flex gap-2.5 sm:gap-3 min-w-0 flex-1">
        <Link
          to={appPath('/project-analysis')}
          className="flex items-center gap-1.5 text-[12px] font-medium text-slate-600 border border-slate-200 rounded-lg px-2.5 py-1 hover:bg-white hover:text-slate-900 transition-colors flex-none self-center shrink-0 bg-white/80"
        >
          <ArrowLeft size={13} /> Back
        </Link>

        <div className="min-w-0 flex-1 py-0.5">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <h1 className="text-base sm:text-[17px] font-bold text-[#0D0D0D] leading-snug">
              {title}
            </h1>
            {category?.trim() ? (
              <span className="bg-blue-50 text-blue-700 ring-1 ring-blue-200 text-[10px] font-bold px-2.5 py-0.5 rounded-full tracking-wide shrink-0">
                {category.trim()}
              </span>
            ) : null}
          </div>

          {lineageMetaLoading ? (
            <div
              className="h-3.5 w-[min(20rem,70vw)] bg-slate-100 animate-pulse rounded mt-1.5"
              aria-hidden
            />
          ) : lineageSegments.length > 0 ? (
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 mt-1.5 text-[11px] sm:text-[12px]">
              {lineageSegments.map((seg, i) => (
                <React.Fragment key={seg.key}>
                  {i > 0 ? (
                    <span
                      className={`${DOT_COLORS[(i - 1) % DOT_COLORS.length]} select-none text-sm leading-none`}
                      aria-hidden
                    >
                      •
                    </span>
                  ) : null}
                  <span className="text-slate-600 min-w-0">
                    <span className="text-slate-400 font-medium">
                      {seg.label}
                    </span>{' '}
                    <span className="text-slate-800 font-medium truncate inline align-baseline max-w-[min(14rem,40vw)]">
                      {seg.value}
                    </span>
                  </span>
                </React.Fragment>
              ))}
            </div>
          ) : null}

          {statusPill ? (
            <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
              <span className="bg-amber-50 text-amber-800 ring-1 ring-amber-200 text-[10px] font-bold px-2.5 py-0.5 rounded-full tracking-wide">
                {statusPill}
              </span>
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex-none w-full lg:w-auto lg:max-w-[min(36rem,48vw)] xl:max-w-2xl shrink-0">
        {periodSlot}
      </div>
    </div>
  )
}
